/**
 * The flat row model the virtualised list renders (PLAN §2.4, §2.6, §2.9).
 * Pure: everything the palette shows is decided here from the search state,
 * so the order, the group caps, Top hit and the empty states are unit-tested
 * without a DOM.
 *
 * Item types and their fixed heights (usePaletteWindow needs them):
 *   header 28 · row 36 · row with a snippet 52 · more / action rows 34 ·
 *   skeleton 36 · error / note / empty 34
 */
import { GLOBAL_SHOWN, refOf } from './types';
import { pickTopHit } from './ranking';

export const HEIGHTS = Object.freeze({ header: 28, row: 36, snippet: 52, more: 34, searchMessages: 34, skeleton: 36, error: 34, note: 34, empty: 44 });
export const SELECTABLE = new Set(['row', 'more', 'searchMessages']);

export function itemHeight(item) {
  if (item.type === 'row') return item.result?.snippet ? HEIGHTS.snippet : HEIGHTS.row;
  return HEIGHTS[item.type] || HEIGHTS.row;
}

const rowItem = (result, section) => ({ type: 'row', key: `${section}|${refOf(result.provider, result.id)}`, result, section });

/**
 * Root view (empty query): Recent, Suggested, Pages — or, in ⌘P quick open,
 * Recent and the open tabs.
 */
export function buildRootRows({ recent = [], suggested = [], pages = [], tabs = [], mode = null, labels }) {
  const out = [];
  const push = (id, label, list) => {
    if (!list.length) return;
    out.push({ type: 'header', key: `h|${id}`, label, groupId: id });
    for (const r of list) out.push(rowItem(r, id));
  };
  push('recent', labels.recent, recent);
  if (mode === 'quickOpen') {
    push('tabs', labels.tabs, tabs);
    return out;
  }
  push('suggested', labels.suggested, suggested);
  push('pages', labels.pages, pages);
  return out;
}

/**
 * Search view. `groups`: Map providerId → { results, status, more, skeleton, error }
 * in provider order (`order` = provider ids sorted by group order).
 */
export function buildSearchRows({
  groups,
  order,
  scope = null,
  text = '',
  labels,
  labelOf,
  messagesAvailable = false,
  offline = false,
  exactKey = null,
}) {
  const out = [];
  const ids = order.filter((id) => groups.has(id));
  let topHit = null;
  if (!scope) {
    const heads = ids.map((id) => groups.get(id).results[0]).filter(Boolean);
    topHit = pickTopHit(heads, { exactKey });
    if (topHit) {
      out.push({ type: 'header', key: 'h|top', label: labels.topHit, groupId: 'top' });
      out.push(rowItem(topHit, 'top'));
    }
  }
  let any = !!topHit;
  for (const id of ids) {
    const g = groups.get(id);
    let results = g.results || [];
    if (topHit && topHit.provider === id) results = results.filter((r) => r.id !== topHit.id);
    const shown = scope ? results : results.slice(0, GLOBAL_SHOWN);
    if (!shown.length && !g.skeleton && !g.error) continue;
    out.push({ type: 'header', key: `h|${id}`, label: labelOf(id), groupId: id });
    for (const r of shown) out.push(rowItem(r, id));
    if (shown.length) any = true;
    if (g.skeleton && !shown.length) out.push({ type: 'skeleton', key: `sk|${id}`, providerId: id });
    if (g.error) out.push({ type: 'error', key: `err|${id}`, providerId: id, label: labels.couldNotSearch(labelOf(id)) });
    if (!scope && g.more) out.push({ type: 'more', key: `more|${id}`, providerId: id, label: labels.showAll(labelOf(id)) });
    if (id === 'messages') {
      const meta = shown[0]?.payload?.meta;
      if (meta && meta.historyDays) out.push({ type: 'note', key: 'note|history', label: labels.historyHidden(meta.historyDays) });
      else if (meta && (meta.partial || !meta.indexComplete)) out.push({ type: 'note', key: 'note|partial', label: labels.partial });
    }
  }
  if (offline && (groups.has('people') || groups.has('messages'))) {
    out.push({ type: 'note', key: 'note|offline', label: labels.offline });
  }
  const canSearchMessages = messagesAvailable && scope !== 'messages' && text.length >= 3 && (!scope || scope === 'chats');
  if (!any) {
    const pending = ids.some((id) => groups.get(id).status === 'loading');
    if (!pending) out.unshift({ type: 'empty', key: 'empty', label: labels.noResults(text) });
  }
  if (canSearchMessages) out.push({ type: 'searchMessages', key: 'searchMessages', label: labels.searchMessages(text) });
  if (!any && !scope && out.every((i) => i.type !== 'skeleton')) {
    out.push({ type: 'note', key: 'note|try', label: labels.tryScopes });
  }
  return { items: out, topHit };
}

export function selectableKeys(items) {
  return items.filter((i) => SELECTABLE.has(i.type)).map((i) => i.key);
}

/** Keep the highlighted row by key across merges; else the first selectable. */
export function pinActive(items, activeKey) {
  const keys = selectableKeys(items);
  if (activeKey && keys.includes(activeKey)) return activeKey;
  return keys[0] || null;
}

export function moveActive(items, activeKey, delta, { wrap = true } = {}) {
  const keys = selectableKeys(items);
  if (!keys.length) return null;
  const i = keys.indexOf(activeKey);
  if (i < 0) return keys[0];
  let j = i + delta;
  if (wrap) j = ((j % keys.length) + keys.length) % keys.length;
  else j = Math.max(0, Math.min(keys.length - 1, j));
  return keys[j];
}

export function countResults(items) {
  return items.filter((i) => i.type === 'row').length;
}
